import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";

const normalizePath = (pathname) => {
  const value = pathname || "/";
  return value.length > 1 ? value.replace(/\/+$/, "") : "/";
};

const matchesPath = (to, pathname, exact = false) => {
  const current = normalizePath(pathname);
  const target = normalizePath(to);
  return !!target && (current === target || (!exact && target !== "/" && current.startsWith(`${target}/`)));
};

function findTrail(navDef, pathname) {
  let best = null;

  (navDef || []).forEach((group) => {
    (group.items || []).forEach((item) => {
      const candidates = item.sub ? item.items || [] : [item];

      candidates.forEach((candidate) => {
        if (!matchesPath(candidate.to, pathname, candidate.exact)) return;
        if (best && candidate.to.length <= best.item.to.length) return;
        best = { group, parent: item.sub ? item : null, item: candidate };
      });
    });
  });

  return best;
}

function findFallback(routes, pathname) {
  const current = normalizePath(pathname);
  for (const route of routes) {
    if (route.re.test(current)) return route.items;
  }
  return null;
}

export default function PageBreadcrumb({ items = [], navDef, pathname, lang = "zh" }) {
  const L = (zh, en) => (lang === "zh" ? zh : en);
  const fallbackTrails = [
    { re: /^\/project\/[^/]+\/subcontracts$/, items: [{ label: L("工程管理", "Engineering"), to: "/board" }, { label: L("分包商管理", "Subcontracts") }] },
    { re: /^\/$/, items: [{ label: L("首页", "Home"), to: "/" }] },
    { re: /^\/todos$/, items: [{ label: L("协作 / 工具", "Collaboration"), to: "/collab" }, { label: L("待办", "To-dos") }] },
    { re: /^\/my-today$/, items: [{ label: L("协作 / 工具", "Collaboration"), to: "/collab" }, { label: L("我的今日必办", "My Today") }] },
    { re: /^\/done-report$/, items: [{ label: L("协作 / 工具", "Collaboration"), to: "/collab" }, { label: L("当日完成明细", "Daily Completion") }] },
    { re: /^\/nudge-stats$/, items: [{ label: L("协作 / 工具", "Collaboration"), to: "/collab" }, { label: L("催办统计", "Nudge Statistics") }] },
    { re: /^\/project\/[^/]+\/inspection$/, items: [{ label: L("暂不开放", "Not Yet Available") }, { label: L("现场巡检", "Site Inspection"), to: "/site-inspections" }, { label: L("详情", "Details") }] },
    { re: /^\/project\/[^/]+\/site-survey$/, items: [{ label: L("暂不开放", "Not Yet Available") }, { label: L("现场调研", "Site Survey"), to: "/site-inspections" }, { label: L("详情", "Details") }] },
    { re: /^\/project\/[^/]+$/, items: [{ label: L("工程管理", "Engineering"), to: "/board" }, { label: L("项目详情", "Project Details") }] },
    { re: /^\/branch-report\/[^/]+$/, items: [{ label: L("概览", "Overview"), to: "/overview" }, { label: L("分公司周报", "Branch Weekly Report") }] },
    { re: /^\/finance\/project\/[^/]+$/, items: [{ label: L("财务管理", "Finance"), to: "/finance-center" }, { label: L("项目财务", "Project Finance"), to: "/finance/projects" }, { label: L("详情", "Details") }] },
    { re: /^\/finance\/imports$/, items: [{ label: L("财务管理", "Finance"), to: "/finance-center" }, { label: L("收付款导入历史", "Import History") }] },
    { re: /^\/finance\/my-card$/, items: [{ label: L("人力资源", "Human Resources"), to: "/hrms/me" }, { label: L("我的信用卡", "My Credit Card") }] },
    { re: /^\/assets\/[^/]+$/, items: [{ label: L("系统管理", "Admin"), to: "/admin/users" }, { label: L("资产详情", "Asset Details") }] },
    { re: /^\/settings\/phase-standards$/, items: [{ label: L("系统管理", "Admin"), to: "/admin/users" }, { label: L("阶段标准工期", "Phase Standards") }] },
    { re: /^\/settings\/project-import$/, items: [{ label: L("系统管理", "Admin"), to: "/admin/users" }, { label: L("历史项目导入", "Project Import") }] },
    { re: /^\/ai$/, items: [{ label: L("协作 / 工具", "Collaboration"), to: "/collab" }, { label: L("AI 智能助手", "AI Assistant") }] },
    { re: /^\/crm\/hall-of-fame$/, items: [{ label: L("销售 / CRM", "Sales / CRM"), to: "/crm/leads" }, { label: L("认领冠军荣誉墙", "Claim Champions") }] },
    { re: /^\/crm\/leads\/[^/]+$/, items: [{ label: L("销售 / CRM", "Sales / CRM"), to: "/crm/leads" }, { label: L("客户线索 CRM", "Leads CRM"), to: "/crm/leads" }, { label: L("详情", "Details") }] },
    { re: /^\/agents\/[^/]+$/, items: [{ label: L("销售 / CRM", "Sales / CRM"), to: "/crm/leads" }, { label: L("电话营销", "Telemarketing"), to: "/agents" }, { label: L("坐席详情", "Agent Details") }] },
    { re: /^\/design\/todo$/, items: [{ label: L("设计部", "Design Dept"), to: "/design/dashboard" }, { label: L("待办任务", "Tasks"), to: "/design/tasks" }, { label: L("今日代办", "Today") }] },
    // 变更待办可由项目经理和新指派绘图人直达，面包屑不引导到可能无权访问的设计总览。
    { re: /^\/design\/changes\/[^/]+$/, items: [{ label: L("设计协作", "Design collaboration") }, { label: L("设计变更", "Design changes") }] },
    { re: /^\/design\/struct-orders$/, items: [{ label: L("设计部", "Design Dept"), to: "/design/dashboard" }, { label: L("结构设计外包", "Structural Outsourcing") }] },
    { re: /^\/warehouses\/scan$/, items: [{ label: L("仓储物流管理", "Warehouse & Logistics"), to: "/warehouse-dashboard" }, { label: L("仓库管理", "Warehouses"), to: "/warehouses" }, { label: L("扫码工作台", "Scan Station") }] },
    { re: /^\/warehouses\/bin\/[^/]+$/, items: [{ label: L("仓储物流管理", "Warehouse & Logistics"), to: "/warehouse-dashboard" }, { label: L("仓库管理", "Warehouses"), to: "/warehouses" }, { label: L("货位复核", "Bin Recheck") }] },
    { re: /^\/hrms\/assets$/, items: [{ label: L("人力资源", "Human Resources"), to: "/hrms/me" }, { label: L("固定资产", "Company Assets") }] },
    { re: /^\/hrms\/transitions$/, items: [{ label: L("人力资源", "Human Resources"), to: "/hrms/me" }, { label: L("入离职编排", "On/Offboarding") }] },
    { re: /^\/hrms\/leaves$/, items: [{ label: L("人力资源", "Human Resources"), to: "/hrms/me" }, { label: L("假勤管理", "Leave Management") }] },
    { re: /^\/hrms\/m\/[^/]+$/, items: [{ label: L("人力资源", "Human Resources"), to: "/hrms/me" }, { label: L("模块", "Module") }] },
  ];
  const fallback = items.length ? null : findFallback(fallbackTrails, pathname);
  const trail = items.length || fallback ? null : findTrail(navDef, pathname);
  const isDetail = trail ? pathname !== trail.item.to : false;
  const derived = fallback || (trail
    ? [
        trail.group?.label,
        trail.parent?.label,
        trail.item?.label,
        isDetail ? (lang === "zh" ? "详情" : "Details") : null,
      ]
    : []);
  const visible = (items.length ? items : derived).filter(Boolean);
  const crumbs = visible.length ? visible : [{ label: L("首页", "Home"), to: "/" }];

  return (
    <div className="flex min-h-7 items-center" data-testid="page-breadcrumb">
      <Breadcrumb>
        <BreadcrumbList className="no-scrollbar flex-nowrap gap-1.5 overflow-x-auto text-[13px] sm:gap-2">
          {crumbs.map((item, index) => {
            const label = typeof item === "string" ? item : item.label;
            const to = typeof item === "string" ? null : item.to;
            const isLast = index === crumbs.length - 1;

            return (
              <React.Fragment key={`${label}-${index}`}>
                {index > 0 && (
                  <BreadcrumbSeparator className="text-slate-300">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </BreadcrumbSeparator>
                )}
                <BreadcrumbItem className="shrink-0 whitespace-nowrap">
                  {isLast ? (
                    <BreadcrumbPage className="font-semibold text-slate-800">
                      {label}
                    </BreadcrumbPage>
                  ) : to ? (
                    <BreadcrumbLink asChild>
                      <Link to={to} className="font-medium text-slate-400 hover:text-brand-600">
                        {label}
                      </Link>
                    </BreadcrumbLink>
                  ) : (
                    <span className="font-medium text-slate-400">{label}</span>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}
